import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockIcon from '@mui/icons-material/Block';
import PageHeader from '../components/common/PageHeader';
import PartnerSelector from '../components/common/PartnerSelector';
import StatusChip from '../components/common/StatusChip';
import LoadingScreen from '../components/common/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import { partnerService } from '../services/partnerService';
import { formatDate, getErrorMessage } from '../utils/format';

export default function ApiKeys() {
  const { user, isAdmin } = useAuth();
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [keyName, setKeyName] = useState('Production Key');
  const [newKey, setNewKey] = useState('');
  const [snack, setSnack] = useState('');

  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        if (isAdmin) {
          const res = await partnerService.list({ limit: 100 });
          const list = res.partners || [];
          setPartners(list);
          if (list[0]) setPartnerId(list[0].id);
        } else if (user?.partnerId) {
          setPartnerId(user.partnerId);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [isAdmin, user?.partnerId]);

  useEffect(() => {
    if (!partnerId) return;
    loadKeys();
  }, [partnerId]);

  const loadKeys = async () => {
    try {
      const list = await partnerService.listApiKeys(partnerId, true);
      setKeys(list);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleCreate = async () => {
    try {
      const result = await partnerService.createApiKey(partnerId, { name: keyName });
      setNewKey(result.apiKey);
      setCreateOpen(false);
      await loadKeys();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleRotate = async keyId => {
    try {
      const result = await partnerService.rotateApiKey(keyId);
      setNewKey(result.apiKey);
      await loadKeys();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleRevoke = async keyId => {
    try {
      await partnerService.revokeApiKey(keyId);
      await loadKeys();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const copyKey = async text => {
    await navigator.clipboard.writeText(text);
    setSnack('Copied to clipboard');
  };

  if (loading) return <LoadingScreen />;

  return (
    <>
      <PageHeader
        title="API Keys"
        subtitle="Create, rotate, and revoke partner API keys"
        action={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {isAdmin && (
              <PartnerSelector partners={partners} value={partnerId} onChange={setPartnerId} />
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              disabled={!partnerId}
            >
              Create Key
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {newKey && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => copyKey(newKey)}>
              Copy
            </Button>
          }
        >
          Store this key securely — it won&apos;t be shown again: <strong>{newKey}</strong>
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Prefix</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" align="center" py={3}>
                      No API keys for this partner
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {keys.map(k => (
                <TableRow key={k.id} hover>
                  <TableCell>{k.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {k.keyPrefix}…
                      <Tooltip title="Copy prefix">
                        <IconButton size="small" onClick={() => copyKey(k.keyPrefix)}>
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={k.status} />
                  </TableCell>
                  <TableCell>{formatDate(k.lastUsedAt)}</TableCell>
                  <TableCell>{formatDate(k.createdAt)}</TableCell>
                  <TableCell align="right">
                    {k.status === 'active' && (
                      <>
                        <Tooltip title="Rotate">
                          <IconButton size="small" onClick={() => handleRotate(k.id)}>
                            <AutorenewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Revoke">
                          <IconButton size="small" color="error" onClick={() => handleRevoke(k.id)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Key Name"
            value={keyName}
            onChange={e => setKeyName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={3000} message={snack} onClose={() => setSnack('')} />
    </>
  );
}
